import { supabase } from '@/lib/supabase/client'
import type { Project } from '../types/project'
import type { ProjectRole } from '@/components/shared/role-chip'
import type { RecordStatus } from '@/components/shared/status-badge'

// Map DB enums to app types
function mapRole(dbRole: string): ProjectRole {
  const map: Record<string, ProjectRole> = {
    super_user: 'super_user',
    admin: 'admin',
    approver: 'approver',
    data_entry: 'data_entry',
    viewer: 'viewer',
  }
  return map[dbRole] ?? 'viewer'
}

function mapStatus(dbStatus: string): RecordStatus {
  const map: Record<string, RecordStatus> = {
    active: 'active',
    paused: 'paused',
    closed: 'closed',
    archived: 'archived',
  }
  return map[dbStatus] ?? 'active'
}

// projects.record_counter is the sequential record-numbering counter, not a
// population count (database_architecture.md §320). Because deletes are soft
// and numbers are never reused, it drifts above the live total — so count the
// records instead. One query + client-side tally avoids an N+1 over projects.
async function fetchLiveRecordCounts(): Promise<Map<string, number>> {
  const { data, error } = await supabase
    .from('records')
    .select('project_id')
    .eq('is_deleted', false)

  if (error) throw error

  const counts = new Map<string, number>()
  for (const r of data ?? []) {
    if (r.project_id) counts.set(r.project_id, (counts.get(r.project_id) ?? 0) + 1)
  }
  return counts
}

// Super User is platform-wide and bypasses membership entirely
// (02_roles_permissions.md §2.5), so it is not reachable through the
// membership join every other role uses.
async function fetchProjectsAsSuperUser(userId: string): Promise<Project[]> {
  const [projectsRes, membershipsRes, recordCounts] = await Promise.all([
    supabase
      .from('projects')
      .select('id, name, code, department, status, color')
      .order('project_number', { ascending: true }),
    supabase
      .from('project_memberships')
      .select('project_id, role, joined_at')
      .eq('user_id', userId)
      .eq('is_removed', false),
    fetchLiveRecordCounts(),
  ])

  if (projectsRes.error) throw projectsRes.error
  if (membershipsRes.error) throw membershipsRes.error

  const membershipByProject = new Map(
    (membershipsRes.data ?? []).map((m) => [m.project_id, m])
  )

  return (projectsRes.data ?? []).map((p) => {
    const membership = membershipByProject.get(p.id)
    return {
      id: p.id,
      name: p.name,
      code: p.code ?? '',
      department: p.department,
      status: mapStatus(p.status),
      // A super user may also hold a real role; prefer it over the implicit one.
      role: membership ? mapRole(membership.role) : 'super_user',
      recordCount: recordCounts.get(p.id) ?? 0,
      lastAccessed: membership?.joined_at ?? null,
      color: p.color,
    } satisfies Project
  })
}

export async function fetchMyProjects(): Promise<Project[]> {
  const { data: sessionData } = await supabase.auth.getSession()
  const userId = sessionData.session?.user?.id
  if (!userId) return []

  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('is_super_user')
    .eq('id', userId)
    .maybeSingle()

  if (profileError) throw profileError
  if (profile?.is_super_user) return fetchProjectsAsSuperUser(userId)

  const [{ data, error }, recordCounts] = await Promise.all([
    supabase
      .from('project_memberships')
      .select(
        `
      role,
      joined_at,
      projects (
        id,
        name,
        code,
        department,
        status,
        color
      )
    `
      )
      .eq('user_id', userId)
      .eq('is_removed', false),
    fetchLiveRecordCounts(),
  ])

  if (error) throw error
  if (!data) return []

  return data
    .filter((m) => m.projects !== null)
    .map((m) => {
      const p = m.projects as {
        id: string
        name: string
        code: string | null
        department: string | null
        status: string
        color: string | null
      }
      return {
        id: p.id,
        name: p.name,
        code: p.code ?? '',
        department: p.department,
        status: mapStatus(p.status),
        role: mapRole(m.role),
        recordCount: recordCounts.get(p.id) ?? 0,
        lastAccessed: m.joined_at,
        color: p.color,
      } satisfies Project
    })
}
